import { gql } from "@apollo/client";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  FILTER_DELIMITER,
  FILTER_TYPES,
  FormTabsTypes,
  SearchAndFilterTypes,
} from "src/constants";
import {
  DefaultColumnsProps,
  getDefaultColumnIdsForTab,
} from "src/scenes/orgAdmin/forms/formTables/columns";
import { maybePluralize } from "src/services/format";
import { isNotNull } from "src/services/predicates";
import { stringsAsEnums } from "src/types/enum";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { useAvelaToast } from "./useAvelaToast";
import { useOrganization } from "./useOrganization";
import { useRemoteDataMutation } from "./useRemoteDataMutation";
import { useRemoteDataQuery } from "./useRemoteDataQuery";
import { useTeam } from "./useTeam";
import { useUserProfile } from "./useUserProfile";

const SAVED_VIEW_FRAGMENT = gql`
  fragment SavedViewFragment on saved_view {
    id
    name
    icon
    form_template_id
    filters
    created_at
    saved_view_sharings_aggregate {
      aggregate {
        count
      }
    }
  }
`;

const GET_SAVED_VIEWS = gql`
  ${SAVED_VIEW_FRAGMENT}
  query GetSavedViews($personId: uuid!, $formTemplateId: uuid!) {
    saved_view(
      where: {
        _and: [
          { form_template_id: { _eq: $formTemplateId } }
          { saved_view_owner: { owner_person: { id: { _eq: $personId } } } }
        ]
      }
    ) {
      ...SavedViewFragment
    }
    saved_view_sharing(
      where: {
        _and: [
          { saved_view: { form_template_id: { _eq: $formTemplateId } } }
          {
            _or: [
              { entity_id: { _eq: $personId } }
              {
                shared_team: { team_members: { person_id: { _eq: $personId } } }
              }
            ]
          }
        ]
      }
    ) {
      id
      entity_id
      permission
      shared_team {
        id
      }
      saved_view {
        ...SavedViewFragment
      }
    }
  }
`;

const CREATE_SAVED_VIEW = gql`
  mutation CreateSavedView($savedView: saved_view_insert_input!) {
    insert_saved_view_one(object: $savedView) {
      id
    }
  }
`;

const UPDATE_SAVED_VIEW = gql`
  mutation UpdateSavedView($id: uuid!, $savedView: saved_view_set_input!) {
    update_saved_view_by_pk(pk_columns: { id: $id }, _set: $savedView) {
      id
    }
  }
`;

const DELETE_SAVED_VIEW = gql`
  mutation DeleteSavedView($id: uuid!) {
    delete_saved_view_sharing(where: { saved_view_id: { _eq: $id } }) {
      affected_rows
    }
    delete_saved_view_by_pk(id: $id) {
      id
    }
  }
`;

const TRANSFER_OWNERSHIP = gql`
  mutation TransferOwnership(
    $savedViewId: uuid!
    $currentOwnerId: uuid!
    $newOwnerId: uuid!
  ) {
    insert_saved_view_sharing(
      objects: [
        {
          saved_view_id: $savedViewId
          entity_id: $currentOwnerId
          permission: Editor
        }
      ]
    ) {
      affected_rows
    }
    delete_saved_view_sharing(
      where: {
        _and: [
          { saved_view_id: { _eq: $savedViewId } }
          { entity_id: { _eq: $newOwnerId } }
        ]
      }
    ) {
      affected_rows
    }
    update_saved_view_owner_by_pk(
      pk_columns: { saved_view_id: $savedViewId }
      _set: { owner_id: $newOwnerId }
    ) {
      saved_view_id
    }
  }
`;

const GET_SHARED_SAVED_VIEWS = gql`
  query GetSharedSavedViews($savedViewId: uuid!) {
    saved_view_sharing(where: { saved_view_id: { _eq: $savedViewId } }) {
      id
      permission
      entity_id
    }
    saved_view_owner_by_pk(saved_view_id: $savedViewId) {
      owner_person {
        id
        first_name
        last_name
        email_address
      }
    }
  }
`;

const GET_MEMBERS_AND_TEAMS = gql`
  query GetMembersAndTeams($organizationId: uuid!) {
    person(
      where: {
        _and: [
          { person_type: { _nin: [guardian, applicant] } }
          { organization_id: { _eq: $organizationId } }
        ]
      }
    ) {
      id
      first_name
      last_name
      email_address
    }
    team(where: { organization_id: { _eq: $organizationId } }) {
      id
      name
      team_members_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

const DELETE_SAVED_VIEW_SHARINGS = gql`
  mutation DeleteSavedViewSharings(
    $savedViewId: uuid!
    $savedViewShareWithIds: [uuid!]!
  ) {
    delete_saved_view_sharing(
      where: {
        _and: [
          { saved_view_id: { _eq: $savedViewId } }
          { entity_id: { _in: $savedViewShareWithIds } }
        ]
      }
    ) {
      affected_rows
    }
  }
`;

const CREATE_SAVED_VIEW_SHARINGS = gql`
  mutation CreateSavedViewSharings(
    $savedViewSharings: [saved_view_sharing_insert_input!]!
  ) {
    insert_saved_view_sharing(
      objects: $savedViewSharings
      on_conflict: {
        constraint: saved_view_sharing_entity_id_saved_view_id_key
        update_columns: [permission]
      }
    ) {
      affected_rows
    }
  }
`;

const UPDATE_SAVED_VIEW_SHARINGS = gql`
  mutation UpdateSavedViewSharings($updates: [saved_view_sharing_updates!]!) {
    update_saved_view_sharing_many(updates: $updates) {
      affected_rows
    }
  }
`;

export const SavedViewConfigSchema = z.object({
  params: z.record(z.string(), z.string()),
  columns: z.array(z.string()).optional(),
});
export type SavedViewConfig = z.infer<typeof SavedViewConfigSchema>;

export type SavedView = Omit<
  GQL.GetSavedViews_saved_view,
  "filters" | "saved_view_sharings_aggregate"
> & {
  filters: SavedViewConfig | null;
  permission: SavedViewPermission;
  sharedCount: number;
};

const PARAMS_TO_SAVE = [
  SearchAndFilterTypes.Tab,
  SearchAndFilterTypes.Search,
  SearchAndFilterTypes.DropoffFormTemplateId,
  ...Object.keys(FILTER_TYPES),
  SearchAndFilterTypes.SortKey,
  SearchAndFilterTypes.SortType,
];

function extractColumnsFromSearchParams(
  searchParams: URLSearchParams,
  defaultColumnsProps: DefaultColumnsProps
) {
  const columns = searchParams
    .get(SearchAndFilterTypes.VisibleColumns)
    ?.split(FILTER_DELIMITER);
  if (columns && columns.length > 0) {
    return columns;
  }
  // If no columns are set, assume the default columns for the tab
  const tabType =
    (searchParams.get(SearchAndFilterTypes.Tab) as FormTabsTypes) ||
    FormTabsTypes.All;
  const allDefaultColumns = getDefaultColumnIdsForTab({
    tabType,
    ...defaultColumnsProps,
  });
  return allDefaultColumns;
}

export function extractSavedViewFilters(
  searchParams: URLSearchParams,
  includeColumns: boolean,
  defaultColumnsProps: DefaultColumnsProps
): SavedViewConfig {
  return {
    params: Object.fromEntries(
      [...searchParams.entries()].filter(([key]) =>
        PARAMS_TO_SAVE.includes(key)
      )
    ),
    ...(includeColumns
      ? {
          columns: extractColumnsFromSearchParams(
            searchParams,
            defaultColumnsProps
          ),
        }
      : {}),
  };
}
export function formatSavedViewParams(
  savedView: SavedView,
  defaultColumnsProps: DefaultColumnsProps
) {
  const params = new URLSearchParams(savedView.filters?.params);
  params.set(SearchAndFilterTypes.SavedView, savedView.id);
  if (savedView.filters?.columns) {
    params.set(
      SearchAndFilterTypes.VisibleColumns,
      savedView.filters.columns.join(FILTER_DELIMITER)
    );
  } else if (savedView.filters?.params[SearchAndFilterTypes.Tab]) {
    // If savedViewFilters.columns doesn't exist, assume the saved view includes all default columns.
    const savedViewTabType =
      (savedView.filters?.params[SearchAndFilterTypes.Tab] as FormTabsTypes) ||
      FormTabsTypes.All;
    const columns = getDefaultColumnIdsForTab({
      tabType: savedViewTabType,
      ...defaultColumnsProps,
    });
    params.set(
      SearchAndFilterTypes.VisibleColumns,
      columns.join(FILTER_DELIMITER)
    );
  }
  return params.toString();
}

export const SavedViewFormSchema = z.object({
  name: z.string(),
  icon: z.string(),
});
export type SavedViewFormType = z.infer<typeof SavedViewFormSchema>;

export type ShareOption = {
  id: string;
  label: string;
  subtext: string;
  searchText: string;
  type: "person" | "team";
};

export type SharePermission = GQL.saved_view_sharing_permission_enum;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SharePermission = GQL.saved_view_sharing_permission_enum;

export type SavedViewPermission = "Owner" | SharePermission;

export type SharedView = {
  sharedWithId: string;
  permission: SharePermission;
};

type UseSavedViews = {
  savedViews: SavedView[];
  handleAddSavedView: (savedView: SavedViewFormType) => void;
  handleUpdateSavedView: (
    savedView: SavedViewFormType,
    savedViewId: string
  ) => void;
  handleDeleteSavedView: (id: string) => void;
};
export function useSavedViews({
  formTemplateId,
  defaultColumnsProps,
}: {
  formTemplateId: string;
  defaultColumnsProps: DefaultColumnsProps;
}): UseSavedViews {
  const profile = useUserProfile();
  const team = useTeam();

  const [searchParams, setSearchParams] = useSearchParams();
  const flags = useFlags(["saved-views-sharing", "saved-view-custom-columns"]);
  const sharedSavedViewsEnabled = flags["saved-views-sharing"].enabled;
  const savedViewCustomColumnsEnabled =
    flags["saved-view-custom-columns"].enabled;

  const personId = profile.toNullable()?.id ?? "";
  const teamId = team.toNullable() ?? "";

  const { remoteData: savedViewData, refetch: refetchSavedViews } =
    useRemoteDataQuery<GQL.GetSavedViews, GQL.GetSavedViewsVariables>(
      GET_SAVED_VIEWS,
      {
        variables: {
          formTemplateId,
          personId,
        },
        skip: !formTemplateId || !personId,
      }
    );
  const savedViews = useMemo(() => {
    if (savedViewData.hasError()) {
      console.error("Error loading saved views:", savedViewData.error);
      return [];
    }

    if (!savedViewData.hasData()) return [];
    // Saved views owned by the user
    const savedViewsOwned = savedViewData.data.saved_view.map((savedView) => ({
      ...savedView,
      permission: "Owner" as SavedViewPermission,
    }));
    // Saved views shared with the user
    const savedViewsSharedMember = sharedSavedViewsEnabled
      ? savedViewData.data.saved_view_sharing.filter(
          (savedViewSharing) => savedViewSharing.entity_id === personId
        )
      : [];
    // Saved views shared with a team that the user is a member of
    const savedViewsSharedTeam = sharedSavedViewsEnabled
      ? savedViewData.data.saved_view_sharing.filter(
          (savedViewSharing) => savedViewSharing.shared_team?.id === teamId
        )
      : [];
    // Saved view may be shared via both individual and team; default to the individual share permission
    const dedupedSavedViews: SavedView[] = _.uniqBy(
      [
        ...savedViewsOwned,
        ...savedViewsSharedMember.map((savedViewSharing) => ({
          ...savedViewSharing.saved_view,
          permission: stringsAsEnums<SharePermission>(
            savedViewSharing.permission
          ),
        })),
        ...savedViewsSharedTeam.map((savedViewSharing) => ({
          ...savedViewSharing.saved_view,
          permission: stringsAsEnums<SharePermission>(
            savedViewSharing.permission
          ),
        })),
      ],
      "id"
    ).map<SavedView>((savedView) => ({
      ...savedView,
      filters: SavedViewConfigSchema.safeParse(savedView.filters).data ?? null,
      sharedCount:
        savedView.saved_view_sharings_aggregate.aggregate?.count ?? 0,
    }));
    return dedupedSavedViews;
  }, [personId, teamId, savedViewData, sharedSavedViewsEnabled]);

  const [createSavedView] = useRemoteDataMutation<
    GQL.CreateSavedView,
    GQL.CreateSavedViewVariables
  >(CREATE_SAVED_VIEW, {
    onCompleted: () => {
      refetchSavedViews();
    },
  });

  const [updateSavedView] = useRemoteDataMutation<
    GQL.UpdateSavedView,
    GQL.UpdateSavedViewVariables
  >(UPDATE_SAVED_VIEW, {
    onCompleted: () => {
      refetchSavedViews();
    },
  });

  const [deleteSavedView] = useRemoteDataMutation<
    GQL.DeleteSavedView,
    GQL.DeleteSavedViewVariables
  >(DELETE_SAVED_VIEW, {
    onCompleted: () => {
      refetchSavedViews();
    },
  });

  const handleAddSavedView = useCallback(
    async (savedView: SavedViewFormType) => {
      try {
        const response = await createSavedView({
          variables: {
            savedView: {
              ...savedView,
              saved_view_owner: {
                data: {
                  owner_id: personId,
                },
              },
              form_template_id: formTemplateId,
              filters: extractSavedViewFilters(
                searchParams,
                savedViewCustomColumnsEnabled,
                defaultColumnsProps
              ),
            },
          },
        });

        const savedViewId = response.data?.insert_saved_view_one?.id;
        if (!savedViewId) {
          throw new Error("Failed to create saved view - no ID returned");
        }

        searchParams.set(SearchAndFilterTypes.SavedView, savedViewId);
        setSearchParams(searchParams);
      } catch (error) {
        console.error(error);
      }
    },
    [
      createSavedView,
      personId,
      formTemplateId,
      searchParams,
      savedViewCustomColumnsEnabled,
      defaultColumnsProps,
      setSearchParams,
    ]
  );

  const handleUpdateSavedView = useCallback(
    async (savedView: SavedViewFormType, savedViewId: string) => {
      try {
        await updateSavedView({
          variables: {
            id: savedViewId,
            savedView,
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [updateSavedView]
  );

  const handleDeleteSavedView = useCallback(
    async (id: string) => {
      try {
        await deleteSavedView({
          variables: { id },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [deleteSavedView]
  );

  return {
    savedViews,
    handleAddSavedView,
    handleUpdateSavedView,
    handleDeleteSavedView,
  };
}

function getPersonFullNameString(person: GQL.GetMembersAndTeams_person) {
  return person.first_name || person.last_name
    ? `${person.first_name} ${person.last_name}`
    : "No name";
}

type UseSavedViewSharing = {
  membersAndTeams: ShareOption[];
  savedViewOwner: ShareOption | null;
  sharedViews: SharedView[];
  refetchSharedSavedViews: () => void;
  saveSharedViews: (props: {
    newSharedWith: ShareOption[];
    newSharedWithPermission: SharePermission | null;
    sharedViewsToUpdate: SharedView[];
    sharedViewsToRemove: string[];
    newOwner: string | null;
  }) => void;
};
export function useSavedViewSharing({
  savedViewId,
}: {
  savedViewId: string;
}): UseSavedViewSharing {
  const toast = useAvelaToast();
  const organization = useOrganization();

  const { remoteData: membersAndTeamsData } = useRemoteDataQuery<
    GQL.GetMembersAndTeams,
    GQL.GetMembersAndTeamsVariables
  >(GET_MEMBERS_AND_TEAMS, {
    variables: {
      organizationId: organization.map((org) => org.id).withDefault(""),
    },
  });

  const membersAndTeams: ShareOption[] = useMemo(() => {
    if (membersAndTeamsData.hasError()) {
      console.error(
        "Error loading members and teams:",
        membersAndTeamsData.error
      );
      return [];
    }

    if (membersAndTeamsData.hasData()) {
      const members = membersAndTeamsData.data.person.map<ShareOption>(
        (person) => {
          const name = getPersonFullNameString(person);
          return {
            id: person.id,
            label: name,
            subtext: person.email_address ?? "No email address",
            searchText: `${name} ${person.email_address}`,
            type: "person",
          };
        }
      );
      const teams = membersAndTeamsData.data.team.map<ShareOption>((team) => {
        const teamMemberCount =
          team.team_members_aggregate.aggregate?.count ?? 0;
        const teamMemberCountString = `Team with ${teamMemberCount} ${maybePluralize(
          teamMemberCount,
          "member"
        )}`;
        return {
          id: team.id,
          label: team.name,
          subtext: teamMemberCountString,
          searchText: team.name,
          type: "team",
        };
      });
      return [...members, ...teams];
    }
    return [];
  }, [membersAndTeamsData]);

  const { remoteData: sharedSavedViewsData, refetch: refetchSharedSavedViews } =
    useRemoteDataQuery<
      GQL.GetSharedSavedViews,
      GQL.GetSharedSavedViewsVariables
    >(GET_SHARED_SAVED_VIEWS, {
      variables: {
        savedViewId: savedViewId,
      },
    });

  const sharedViews: SharedView[] = useMemo(() => {
    if (sharedSavedViewsData.hasError()) {
      console.error(
        "Error loading shared saved views:",
        sharedSavedViewsData.error
      );
      return [];
    }

    if (sharedSavedViewsData.hasData()) {
      const sharedSavedViewsFiltered =
        sharedSavedViewsData.data.saved_view_sharing
          .map<SharedView | null>((sharedSavedView) => {
            const sharedWith = membersAndTeams.find(
              (memberOrTeam) => memberOrTeam.id === sharedSavedView.entity_id
            );
            return sharedWith
              ? {
                  sharedWithId: sharedSavedView.entity_id,
                  permission: sharedSavedView.permission,
                }
              : null;
          })
          .filter(isNotNull);
      return _.sortBy(sharedSavedViewsFiltered, "label");
    }
    return [];
  }, [membersAndTeams, sharedSavedViewsData]);

  const savedViewOwner: ShareOption | null = useMemo(() => {
    if (sharedSavedViewsData.hasData()) {
      const ownerData =
        sharedSavedViewsData.data.saved_view_owner_by_pk?.owner_person;
      return ownerData
        ? {
            id: ownerData.id,
            label: getPersonFullNameString(ownerData),
            subtext: ownerData.email_address ?? "No email address",
            searchText: `${ownerData.first_name} ${ownerData.last_name} ${ownerData.email_address}`,
            type: "person",
          }
        : null;
    }
    return null;
  }, [sharedSavedViewsData]);

  const [deleteSavedViewSharings] = useRemoteDataMutation<
    GQL.DeleteSavedViewSharings,
    GQL.DeleteSavedViewSharingsVariables
  >(DELETE_SAVED_VIEW_SHARINGS, { refetchQueries: [GET_SAVED_VIEWS] });

  const [createSavedViewSharings] = useRemoteDataMutation<
    GQL.CreateSavedViewSharings,
    GQL.CreateSavedViewSharingsVariables
  >(CREATE_SAVED_VIEW_SHARINGS, { refetchQueries: [GET_SAVED_VIEWS] });

  const [updateSavedViewSharings] = useRemoteDataMutation<
    GQL.UpdateSavedViewSharings,
    GQL.UpdateSavedViewSharingsVariables
  >(UPDATE_SAVED_VIEW_SHARINGS, { refetchQueries: [GET_SAVED_VIEWS] });

  const [transferOwnership] = useRemoteDataMutation<
    GQL.TransferOwnership,
    GQL.TransferOwnershipVariables
  >(TRANSFER_OWNERSHIP, { refetchQueries: [GET_SAVED_VIEWS] });

  const saveSharedViews = useCallback(
    async ({
      newSharedWith,
      newSharedWithPermission,
      sharedViewsToUpdate,
      sharedViewsToRemove,
      newOwner,
    }: {
      newSharedWith: ShareOption[];
      newSharedWithPermission: SharePermission | null;
      sharedViewsToUpdate: SharedView[];
      sharedViewsToRemove: string[];
      newOwner: string | null;
    }) => {
      try {
        if (sharedViewsToRemove.length > 0) {
          await deleteSavedViewSharings({
            variables: {
              savedViewId,
              savedViewShareWithIds: sharedViewsToRemove,
            },
          });
        }
        if (newSharedWith.length > 0 && newSharedWithPermission) {
          await createSavedViewSharings({
            variables: {
              savedViewSharings: newSharedWith.map((sharedWith) => ({
                saved_view_id: savedViewId,
                entity_id: sharedWith.id,
                permission: newSharedWithPermission,
              })),
            },
          });
        }
        if (sharedViewsToUpdate.length > 0) {
          await updateSavedViewSharings({
            variables: {
              updates: sharedViewsToUpdate.map((sharedView) => ({
                where: {
                  saved_view_id: { _eq: savedViewId },
                  entity_id: { _eq: sharedView.sharedWithId },
                },
                _set: {
                  permission: sharedView.permission,
                },
              })),
            },
          });
        }
        if (newOwner && savedViewOwner?.id) {
          await transferOwnership({
            variables: {
              savedViewId: savedViewId,
              currentOwnerId: savedViewOwner.id,
              newOwnerId: newOwner,
            },
          });
        }
        refetchSharedSavedViews();
        toast({
          title: "Saved view successfully shared",
          description:
            "The saved view has been shared with the selected teams or members",
          status: "success",
        });
      } catch (error) {
        console.error(error);
        toast({
          title: "Failed to share saved view",
          description: "Please try again",
          status: "error",
        });
      }
    },
    [
      savedViewId,
      savedViewOwner,
      deleteSavedViewSharings,
      createSavedViewSharings,
      updateSavedViewSharings,
      transferOwnership,
      refetchSharedSavedViews,
      toast,
    ]
  );

  return {
    membersAndTeams,
    savedViewOwner,
    sharedViews,
    refetchSharedSavedViews,
    saveSharedViews,
  };
}
